import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { Coupon, CustomerDiscount } from "@/api/types";
import { Badge, Button, Input, Select, tx } from "@/components/ui";
import { DateInput } from "@/components/DateInput";
import { fmtDate } from "@/lib/dates";

const money = (v: string | number) => (Math.round(Number(v) * 100) / 100).toFixed(2);

/** Customer drawer (P10 §27.5): the discounts on this profile — pending ones apply themselves at the next check-out. */
export function CustomerDiscounts({ brandId, customerId }: { brandId: string; customerId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["discounts", customerId], queryFn: () => api<CustomerDiscount[]>(`/app/customers/${customerId}/discounts`) });
  const coupons = useQuery({ queryKey: ["coupons", brandId, "active"], queryFn: () => api<Coupon[]>(`/app/coupons?brand_id=${brandId}&active=true`) });
  const [couponId, setCouponId] = useState("");
  const [scope, setScope] = useState<"next_visit" | "until">("next_visit");
  const [until, setUntil] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["discounts", customerId] });
  const grant = useMutation({
    mutationFn: () => api(`/app/customers/${customerId}/discounts`, { method: "POST", body: JSON.stringify({ coupon_id: couponId, scope, valid_until: scope === "until" ? until || null : null, note: note || null }) }),
    onSuccess: () => { setCouponId(""); setNote(""); setError(null); invalidate(); }, onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  const revoke = useMutation({ mutationFn: (id: string) => api(`/app/customer-discounts/${id}/revoke`, { method: "POST" }), onSuccess: invalidate });
  const label = (c: Coupon) => `${tx(c.name, i18n.language) || c.code || ""}${c.code ? ` (${c.code})` : ""} · ${c.kind === "percent" ? `−${Number(c.value)}%` : `−${money(c.value)} €`}`;
  const tone = (s: string) => (s === "pending" ? "green" : s === "used" ? "gray" : "red");
  return (
    <div className="space-y-2 rounded-lg border p-2">
      <div className="font-medium">{t("retail.discounts")}</div>
      {list.data?.length === 0 && <p className="text-xs text-muted">{t("retail.noDiscounts")}</p>}
      {list.data?.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs">
          <Badge tone={tone(d.status)}>{t(`retail.dstatus.${d.status}`)}</Badge>
          <span>{label(d.coupon)}</span>
          <span className="text-muted">{d.scope === "until" && d.valid_until ? t("retail.untilDate", { date: fmtDate(d.valid_until, i18n.language) }) : t("retail.nextVisit")}{d.note ? ` · ${d.note}` : ""}{d.campaign_id ? ` · ${t("retail.fromCampaign")}` : ""}</span>
          {d.status === "pending" && <button type="button" className="ml-auto underline" onClick={() => revoke.mutate(d.id)}>{t("retail.revoke")}</button>}
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2 border-t pt-2 text-xs">
        <Select className="!w-auto" value={couponId} onChange={(e) => setCouponId(e.target.value)}><option value="">{t("retail.pickCoupon")}</option>{(coupons.data ?? []).map((c) => <option key={c.id} value={c.id}>{label(c)}</option>)}</Select>
        <Select className="!w-auto" value={scope} onChange={(e) => setScope(e.target.value as "next_visit" | "until")}><option value="next_visit">{t("retail.nextVisit")}</option><option value="until">{t("retail.untilScope")}</option></Select>
        {scope === "until" && <DateInput className="!w-36" value={until} onChange={(e) => setUntil(e.target.value)} />}
        <Input className="!w-40" placeholder={t("customers.adjustNote")} value={note} onChange={(e) => setNote(e.target.value)} />
        <Button type="button" variant="secondary" size="sm" disabled={!couponId || grant.isPending || (scope === "until" && !until)} onClick={() => grant.mutate()}>{t("retail.grant")}</Button>
      </div>
      {coupons.data?.length === 0 && <p className="text-xs text-muted">{t("retail.noCoupons")}</p>}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
